import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, exhaustMap, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { closingAdjustmentActions } from '../actions/closing-adjustment.actions';
import { projectAccountsActions } from '../actions/project-accounts.actions';
import {
    selectAdjustmentActiveFilter,
    selectAdjustmentSelectedYear,
} from '../selectors/closing-adjustment.selectors';
import { CLOSING_ADJUSTMENT_FEATURE_KEY } from '../reducer/closing-adjustment.feature-key';
import { ClosingAdjustmentState } from '../reducer/closing-adjustment.reducer';
import { ClosingAdjustmentService } from '../services/closing-adjustment.service';
import { AdjustmentFilterKey, AdjustmentState } from '../types/closing-adjustments.types';

/**
 * REV-21375 — the effects of the correction-booking slice (ADR-0015).
 *
 * ⚠️ **Every write RELOADS the list.** Not a courtesy refresh: a create changes the counts and all
 * three result figures, a transfer changes states and the transfer options, and a discard removes a
 * row from every total. Re-asking the server is the only way those stay consistent — and the result
 * figures in particular are a pure function of the CLOSING's accounts (§11), which the client cannot
 * recompute.
 */

/** The generic refusal key, for a failure that carries no key of its own. */
const GENERIC_ERROR_KEY = 'abcp_adjustments_error';

/** The four pills, mapped to the state the server filters on. `all` sends none. */
const FILTER_STATE: Record<AdjustmentFilterKey, AdjustmentState | null> = {
    all: null,
    released: 'released',
    // Each pill maps to ONE state, never a union. `Gebucht` is `booked` alone and `Übertragen` is
    // `transferred` alone: a transferred correction is a distinct, named state whose whole reason for
    // existing is being distinguishable (§2), which is also why it earns a pill of its own.
    transferred: 'transferred',
    booked: 'booked',
    // `Verworfen` sends NO state: it shows the withdrawn rows whatever state they carry. The view
    // flag travels as its own request parameter.
    discarded: null,
};

/** Which pill asks for the withdrawn population rather than the live one. */
const FILTER_DISCARDED: Record<AdjustmentFilterKey, boolean> = {
    all: false,
    released: false,
    transferred: false,
    booked: false,
    discarded: true,
};

@Injectable()
export class ClosingAdjustmentEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly service: ClosingAdjustmentService,
    ) {}

    /**
     * The closing-wide read.
     *
     * `switchMap`: a fast second pill click must CANCEL the first request, not race it. With `mergeMap`
     * a slow response for `all` could land after a fast one for `open` and leave the table showing rows
     * the active pill does not describe — the trap the document slice records for the same reason.
     */
    public readonly loadAdjustments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingAdjustmentActions.loadAdjustments),
            switchMap(({ projectId, fiscalYear, filter: pill }) =>
                this.service.listAdjustments(projectId, fiscalYear, FILTER_STATE[pill], FILTER_DISCARDED[pill]).pipe(
                    map((response) => closingAdjustmentActions.loadAdjustmentsSuccess({ response })),
                    catchError(() => of(closingAdjustmentActions.loadAdjustmentsFailure())),
                ),
            ),
        ),
    );

    /**
     * Re-ask for the CURRENT year and pill, which the slice already holds.
     *
     * The indirection earns itself: every caller that is not the gesture itself — the tab page on
     * mount, every write's follow-up — knows the project and not what is on screen. Hardcoding a pill
     * at those call sites is how a reload resets the surface the user was looking at.
     */
    /**
     * A write also reloads the IN-ACCOUNT list — when one is open.
     *
     * ⚠️ **This replaces a race.** The account pane used to ask for its own reload in the same
     * breath as the create, straight after dispatching it. Both are asynchronous and neither waits
     * for the other, so the list was re-read while the write was still in flight and came back
     * WITHOUT the new row — which is why a correction entered from an account appeared only after
     * leaving the page and coming back. Keyed on the SUCCESS action, the reload cannot outrun the
     * write that caused it.
     *
     * Filtered on `accountId`, so this stays silent unless a pane has actually loaded a list: the
     * reducer clears the branch when the pane is left, and a write from the closing-wide register
     * must not fire a second request for an account nobody is looking at.
     *
     * `fiscalYear: null` deliberately keeps whatever year the pane is showing — re-asking for the
     * current year here would silently pull a user out of a prior year they had selected.
     */
    public readonly reloadAccountAfterWrite$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                closingAdjustmentActions.createAdjustmentSuccess,
                closingAdjustmentActions.editAdjustmentSuccess,
                closingAdjustmentActions.discardAdjustmentSuccess,
                closingAdjustmentActions.restoreAdjustmentSuccess,
                closingAdjustmentActions.transferAdjustmentsSuccess,
                closingAdjustmentActions.resolveAmbiguitySuccess,
            ),
            withLatestFrom(this.accountBranch$()),
            filter(([, account]) => account.accountId !== null && account.projectId !== null),
            map(([, account]) =>
                closingAdjustmentActions.loadAccountAdjustments({
                    projectId: account.projectId as string,
                    accountId: account.accountId as string,
                    fiscalYear: null,
                    background: true,
                }),
            ),
        ),
    );

    public readonly reloadAdjustments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingAdjustmentActions.reloadAdjustments),
            withLatestFrom(
                this.store.select(selectAdjustmentSelectedYear),
                this.store.select(selectAdjustmentActiveFilter),
            ),
            map(([{ projectId }, fiscalYear, pill]) =>
                closingAdjustmentActions.loadAdjustments({ projectId, fiscalYear, filter: pill }),
            ),
        ),
    );

    /** A pill change RELOADS: which rows exist for a pill is the server's decision, not a predicate. */
    public readonly reloadOnFilter$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingAdjustmentActions.setFilter),
            withLatestFrom(this.store.select(selectAdjustmentSelectedYear), this.projectId$()),
            filter(([, , projectId]) => projectId !== null),
            map(([{ filter: pill }, fiscalYear, projectId]) =>
                closingAdjustmentActions.loadAdjustments({
                    projectId: projectId as string,
                    fiscalYear,
                    filter: pill,
                }),
            ),
        ),
    );

    /**
     * A year change reloads too — and it must: the rows, the counts, the result figures and `readOnly`
     * are all per year, and a prior year is a different surface rather than a filtered one (§10).
     */
    public readonly reloadOnFiscalYear$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingAdjustmentActions.setFiscalYear),
            withLatestFrom(this.store.select(selectAdjustmentActiveFilter), this.projectId$()),
            filter(([, , projectId]) => projectId !== null),
            map(([{ fiscalYear }, pill, projectId]) =>
                closingAdjustmentActions.loadAdjustments({
                    projectId: projectId as string,
                    fiscalYear,
                    filter: pill,
                }),
            ),
        ),
    );

    /**
     * ⚠️ There is deliberately NO reload on `setSearchTerm`.
     *
     * The document hub reloads on a search because the SERVER recomputes its pill counts over the term.
     * Here the four counts are a property of the closing-year and do not move with a search — a count
     * that did would always equal the visible row count — and the list is one fiscal year of one
     * closing. So the search is a client-side narrowing in the selector, and a keystroke costs nothing.
     */

    public readonly loadAdjustment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingAdjustmentActions.loadAdjustment),
            switchMap(({ projectId, adjustmentId }) =>
                this.service.getAdjustment(projectId, adjustmentId).pipe(
                    map((response) => closingAdjustmentActions.loadAdjustmentSuccess({ response })),
                    catchError(() => of(closingAdjustmentActions.loadAdjustmentFailure())),
                ),
            ),
        ),
    );

    /**
     * Entering a correction.
     *
     * `exhaustMap`: a double click on `Buchung erfassen` must not create two corrections. The button
     * also disables itself while the write is in flight, and that is comfort — this is the guard.
     */
    public readonly createAdjustment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingAdjustmentActions.createAdjustment),
            exhaustMap(({ projectId, request }) =>
                this.service.createAdjustment(projectId, request).pipe(
                    map((response) => closingAdjustmentActions.createAdjustmentSuccess({ response })),
                    catchError((error: unknown) =>
                        of(closingAdjustmentActions.createAdjustmentFailure({ errorKey: errorKeyOf(error) })),
                    ),
                ),
            ),
        ),
    );

    public readonly editAdjustment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingAdjustmentActions.editAdjustment),
            exhaustMap(({ projectId, adjustmentId, request }) =>
                this.service.editAdjustment(projectId, adjustmentId, request).pipe(
                    map((response) => closingAdjustmentActions.editAdjustmentSuccess({ response })),
                    catchError((error: unknown) =>
                        of(closingAdjustmentActions.editAdjustmentFailure({ errorKey: errorKeyOf(error) })),
                    ),
                ),
            ),
        ),
    );

    public readonly restoreAdjustment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingAdjustmentActions.restoreAdjustment),
            // The same operator the discard beside it uses, so two writes on this surface behave
            // alike rather than one racing and the other queueing.
            exhaustMap(({ projectId, adjustmentId }) =>
                this.service.restoreAdjustment(projectId, adjustmentId).pipe(
                    map(() => closingAdjustmentActions.restoreAdjustmentSuccess()),
                    catchError((error: unknown) =>
                        of(closingAdjustmentActions.restoreAdjustmentFailure({ errorKey: errorKeyOf(error) })),
                    ),
                ),
            ),
        ),
    );

    public readonly discardAdjustment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingAdjustmentActions.discardAdjustment),
            exhaustMap(({ projectId, adjustmentId }) =>
                this.service.discardAdjustment(projectId, adjustmentId).pipe(
                    map(() => closingAdjustmentActions.discardAdjustmentSuccess()),
                    catchError((error: unknown) =>
                        of(closingAdjustmentActions.discardAdjustmentFailure({ errorKey: errorKeyOf(error) })),
                    ),
                ),
            ),
        ),
    );

    /**
     * The transfer.
     *
     * `exhaustMap` and an IDEMPOTENCY KEY, and both are needed: `exhaustMap` drops a second dispatch
     * while the first is in flight, and the key makes the retry of an interrupted request safe on the
     * server. For bookings a double delivery is not a blemish (§3), so the guard is at both ends.
     *
     * The key is minted PER DISPATCH rather than per selection, because that is what it means: a retry
     * of the same request carries the same key; a new selection is a new request.
     */
    public readonly transferAdjustments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingAdjustmentActions.transferAdjustments),
            exhaustMap(({ projectId, adjustmentIds, method }) =>
                this.service
                    .transferAdjustments(projectId, adjustmentIds, method, idempotencyKey(projectId, adjustmentIds))
                    .pipe(
                        map((response) => closingAdjustmentActions.transferAdjustmentsSuccess({ response })),
                        catchError((error: unknown) =>
                            of(closingAdjustmentActions.transferAdjustmentsFailure({ errorKey: errorKeyOf(error) })),
                        ),
                    ),
            ),
        ),
    );

    /** The ONE write that leads to `Gebucht` — among candidates the reconciliation found (§4). */
    public readonly resolveAmbiguity$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingAdjustmentActions.resolveAmbiguity),
            exhaustMap(({ projectId, adjustmentId, journalEntryId }) =>
                this.service.resolveAmbiguity(projectId, adjustmentId, journalEntryId).pipe(
                    map((response) => closingAdjustmentActions.resolveAmbiguitySuccess({ response })),
                    catchError((error: unknown) =>
                        of(closingAdjustmentActions.resolveAmbiguityFailure({ errorKey: errorKeyOf(error) })),
                    ),
                ),
            ),
        ),
    );

    /**
     * Every successful write re-reads the list.
     *
     * ONE effect for all five rather than a reload inside each, so a sixth write cannot be added
     * without it — the same construction the journal sink uses to cover a fifth importer.
     */
    public readonly reloadAfterWrite$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                closingAdjustmentActions.createAdjustmentSuccess,
                closingAdjustmentActions.editAdjustmentSuccess,
                closingAdjustmentActions.discardAdjustmentSuccess,
                // A restored correction rejoins every live list, so the same reload applies.
                closingAdjustmentActions.restoreAdjustmentSuccess,
                closingAdjustmentActions.transferAdjustmentsSuccess,
                closingAdjustmentActions.resolveAmbiguitySuccess,
            ),
            withLatestFrom(this.projectId$()),
            filter(([, projectId]) => projectId !== null),
            map(([, projectId]) =>
                closingAdjustmentActions.reloadAdjustments({ projectId: projectId as string }),
            ),
        ),
    );

    /**
     * A finished ERP pull re-reads the list too.
     *
     * ⚠️ This is the ONE thing that changes a correction without the user touching this register: the
     * journal reconciliation runs INSIDE the ERP sync (ADR-0015 §2), so `Übertragen` becomes
     * `Gebucht` — or picks up an ambiguity flag — while the surface is showing figures it read
     * before the pull.
     *
     * Without this effect the rows and the filter counts stayed as they were until something else
     * happened to reload: clicking a filter pill did it, which is why the count looked like it only
     * updated when you clicked `Gebucht`. The proof of posting had already arrived; the screen just
     * had not been told.
     *
     * Listening to the ACCOUNTS slice from this one is deliberate. The alternative — having the host
     * page dispatch a reload after its sync — puts the rule in whichever screen happens to own the
     * button, and there is more than one way to reach a sync. The dependency is honest: this slice
     * genuinely depends on that event.
     */
    public readonly reloadAfterErpSync$ = createEffect(() =>
        this.actions$.pipe(
            ofType(projectAccountsActions.syncSuccess),
            withLatestFrom(this.projectId$()),
            // Only when THIS register is the one the sync was for. A sync for another closing must
            // not reload a surface the user has open on a different project.
            filter(([action, projectId]) => projectId !== null && action.projectId === projectId),
            map(([, projectId]) =>
                closingAdjustmentActions.reloadAdjustments({ projectId: projectId as string }),
            ),
        ),
    );

    /**
     * A finished ERP pull also retires the transfer banner.
     *
     * That banner exists to say ONE thing the user has to read: the proof of posting follows with the
     * next ERP refresh. Once a pull has run, that sentence is spent -- the proof either arrived or was
     * not found, and both answers are now on the row itself. Left standing, it promises a refresh that
     * has already happened, directly under a correction that already reads `Gebucht`.
     *
     * Its own effect rather than a second action inside the reload above: the two are different
     * claims. One says the list is stale, this one says a message has expired, and a later change to
     * either should not silently move the other.
     *
     * Not scoped by project, unlike the reload: the banner belongs to whatever the slice last
     * transferred, and a pull on any closing means the user has moved on from it.
     */
    public readonly clearTransferBannerAfterErpSync$ = createEffect(() =>
        this.actions$.pipe(
            ofType(projectAccountsActions.syncSuccess),
            map(() => closingAdjustmentActions.clearTransferResult()),
        ),
    );

    /**
     * The IN-ACCOUNT list (REV-21364).
     *
     * `switchMap`, like the register's reads: a second account opened while the first is still in
     * flight must cancel it, not race it. The reducer also stamps the requested `accountId`, so a
     * response that does arrive late cannot be applied to a different account.
     */
    public readonly loadAccountAdjustments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingAdjustmentActions.loadAccountAdjustments),
            switchMap((action) =>
                this.service.listAccountAdjustments(action.projectId, action.accountId, action.fiscalYear).pipe(
                    map((response) =>
                        closingAdjustmentActions.loadAccountAdjustmentsSuccess({ response }),
                    ),
                    catchError(() => of(closingAdjustmentActions.loadAccountAdjustmentsFailure())),
                ),
            ),
        ),
    );

    /**
     * The slice's own `projectId`.
     *
     * Read through the feature selector rather than carried on every action: the write actions know the
     * project (their caller does), and the RELOAD that follows them must use the same one — taking it
     * from state is what stops a stale route parameter reloading a different closing.
     */
    /**
     * The in-account branch's two reload keys, for the effect above.
     *
     * Typed narrowly rather than as `ReturnType<Store['select']>` — that is `Observable<unknown>`, and
     * the effect reads two properties off the value.
     */
    private accountBranch$(): Observable<{ accountId: string | null; projectId: string | null }> {
        return this.store.select((root: Record<string, unknown>) => {
            const account = (root[CLOSING_ADJUSTMENT_FEATURE_KEY] as ClosingAdjustmentState | undefined)?.account;

            return { accountId: account?.accountId ?? null, projectId: account?.projectId ?? null };
        });
    }

    private projectId$(): ReturnType<Store['select']> {
        return this.store.select(
            (root: Record<string, unknown>) =>
                (root[CLOSING_ADJUSTMENT_FEATURE_KEY] as ClosingAdjustmentState | undefined)?.projectId ?? null,
        );
    }
}

/**
 * The refusal KEY from an HTTP error, or the generic one.
 *
 * The backend answers `{ messageKey: 'abcp_adjustments_error_…' }` and never prose (AC13), so this
 * reads that field and falls back rather than surfacing a status code or a server message: a foreign
 * message tells the user nothing and tells an attacker about our layers.
 */
function errorKeyOf(error: unknown): string {
    const body = (error as { error?: { messageKey?: unknown } } | null)?.error;
    const key = body?.messageKey;

    return typeof key === 'string' && key.length > 0 ? key : GENERIC_ERROR_KEY;
}

/**
 * The `Idempotency-Key` of one transfer request.
 *
 * Derived from the project and the SELECTION rather than random, so that a retry of the same gesture —
 * the user pressing the same target again after a timeout — carries the same key and the server can
 * recognise it. A random key would make every retry a new request, which is exactly what the header
 * exists to prevent for a flow that posts into a ledger.
 *
 * The ids are sorted, so the same selection made in a different order is the same key.
 */
function idempotencyKey(projectId: string, adjustmentIds: readonly string[]): string {
    return `${projectId}:${[...adjustmentIds].sort().join(',')}`;
}
